import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { 
  BeakerIcon, 
  ChartBarIcon, 
  ShieldCheckIcon, 
  CogIcon, 
  DocumentTextIcon, 
  ClipboardIcon,
  CpuChipIcon 
} from '@heroicons/react/24/outline';

type FeatureItem = {
  name: string;
  description: string;
  icon: React.ComponentType<React.SVGProps<SVGSVGElement>>;
  href: string;
};

const features: FeatureItem[] = [
  {
    name: 'Clinical Microbiology Analysis',
    description:
      'Automated detection and identification of pathogens from laboratory samples with classification by type and detailed clinical context.',
    icon: BeakerIcon,
    href: '/clinical-microbiology',
  },
  {
    name: 'Antimicrobial Resistance Analysis',
    description:
      'Detection of resistance genes with our proprietary scoring system (0-100) and classification by antibiotic class for informed treatment decisions.',
    icon: ShieldCheckIcon,
    href: '/antimicrobial-resistance',
  },
  {
    name: 'Treatment Recommendations',
    description:
      'Evidence-based treatment options with dosage, route, frequency, and duration, plus alternative treatments and adverse reaction scoring.',
    icon: ClipboardIcon,
    href: '/treatment-recommendations',
  },
  {
    name: 'Complexity Assessment',
    description:
      'Infection complexity scoring (0-10) with visual representation and classification to guide clinical decision-making.',
    icon: ChartBarIcon,
    href: '/complexity-assessment',
  },
  {
    name: 'Monitoring Plans',
    description:
      'Customized monitoring recommendations with timeframe-specific guidelines, allergy tracking, and follow-up protocols.',
    icon: CogIcon,
    href: '/monitoring-plans',
  },
  {
    name: 'Interactive Reporting',
    description:
      'Modern, tabbed interface with interactive charts, expandable sections, and mobile-friendly design for accessing complex information anywhere.',
    icon: DocumentTextIcon,
    href: '/interactive-reporting',
  },
  {
    name: 'Pharmacogenomics (PGx) Testing',
    description:
      'Personalized medicine through genetic analysis of drug metabolism, efficacy, and adverse reactions to optimize therapeutic outcomes.',
    icon: CpuChipIcon,
    href: 'https://pgx.reasonshield.com',
  },
];

const Features: React.FC = () => {
  return (
    <div id="features" className="py-12 bg-background-alt">
      <div className="max-w-container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="lg:text-center">
          <h2 className="text-base text-primary font-semibold tracking-wide uppercase">Features</h2>
          <p className="mt-2 text-3xl leading-8 font-extrabold tracking-tight text-text sm:text-4xl">
            Comprehensive Lab Results Processing
          </p>
          <p className="mt-4 max-w-2xl text-xl text-text lg:mx-auto">
            ReasonShield transforms complex laboratory data into actionable clinical insights with our advanced molecular and pharmacogenomics platforms.
          </p>
        </div>

        <div className="mt-10">
          <dl className="space-y-10 md:space-y-0 md:grid md:grid-cols-2 md:gap-x-8 md:gap-y-10">
            {features.map((feature, index) => (
              <motion.div 
                key={feature.name}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="relative"
              >
                {feature.href.startsWith('http') ? (
                  <a href={feature.href} target="_blank" rel="noopener noreferrer" className="block hover:no-underline">
                    <dt className="group cursor-pointer">
                      <div className="absolute flex items-center justify-center h-12 w-12 rounded-md bg-primary text-text-light group-hover:bg-accent transition-colors duration-300">
                        <feature.icon className="h-6 w-6" aria-hidden="true" />
                      </div>
                      <p className="ml-16 text-lg leading-6 font-medium text-text group-hover:text-primary transition-colors duration-300">{feature.name}</p>
                    </dt>
                    <dd className="mt-2 ml-16 text-base text-text">{feature.description}</dd>
                  </a>
                ) : (
                  <Link to={feature.href} className="block hover:no-underline">
                    <dt className="group cursor-pointer">
                      <div className="absolute flex items-center justify-center h-12 w-12 rounded-md bg-primary text-text-light group-hover:bg-accent transition-colors duration-300">
                        <feature.icon className="h-6 w-6" aria-hidden="true" />
                      </div>
                      <p className="ml-16 text-lg leading-6 font-medium text-text group-hover:text-primary transition-colors duration-300">{feature.name}</p>
                    </dt>
                    <dd className="mt-2 ml-16 text-base text-text">{feature.description}</dd>
                  </Link>
                )}
              </motion.div>
            ))}
          </dl>
        </div>
      </div>
    </div>
  );
};

export default Features; 